import { useCallback, useRef } from 'react';
import { useOpenWorkspacePanel } from '../panels/use-open-workspace-panel';
import { WorkspacePanel } from '../panels/workspace-panel';
import { SENSOR_PANEL, SensorWorkspacePanel } from './client-only-panel-types';

export type OpenSensorPanelArgs = {
    sensorId: string | undefined;
};

export type OpenSensorPanel = (args: OpenSensorPanelArgs) => void;

/**
 * Opens the camera panel for one sensor, or focuses it if it is already open.
 *
 * The explicit `matchExistingPanel` is load-bearing. `SENSOR` is an `ENTITY`-category panel type,
 * so `shouldBringExistingPanelToFront` returns true for it — and the default matcher in
 * `useOpenWorkspacePanel` matches on panel *type* alone. Without a per-sensor matcher, clicking a
 * second camera would focus the first camera's panel instead of opening its own.

 */
export function useOpenSensorPanel(): OpenSensorPanel {
    // `useOpenWorkspacePanel` returns a fresh closure every render. The map click handler takes
    // this callback as an effect dependency and re-registers its `mapView.on('click')` listener
    // whenever it changes, so it has to be stable. A ref keeps the identity fixed without going
    // stale, the same way `useIntegratedStreams` holds `onReconnect`.
    const openWorkspacePanel = useOpenWorkspacePanel();
    const openWorkspacePanelRef = useRef(openWorkspacePanel);
    openWorkspacePanelRef.current = openWorkspacePanel;

    return useCallback(({ sensorId }: OpenSensorPanelArgs) => {
        if (!sensorId) {
            return;
        }

        openWorkspacePanelRef.current({
            workspacePanelConfig: {
                workspacePanelType: SENSOR_PANEL,
                // Cast contained here, as with the other two client-only panels: the literal has
                // no enum member for `panelState` to narrow against.
                panelState: { sensorId } as never,
            },
            matchExistingPanel: (workspacePanel: WorkspacePanel) => {
                if (workspacePanel.workspacePanelType !== SENSOR_PANEL) {
                    return false;
                }
                // Same narrowing the MEDIA_ATTACHMENTS matcher uses: the matcher signature is
                // generic over panel type, so panelState has to be read through the cast.
                const sensorPanel = workspacePanel as SensorWorkspacePanel;
                return sensorPanel.getPanelState('sensorId') === sensorId;
            },
        });
    }, []);
}
